import { ChevronLeft } from "@material-symbols-svg/react/rounded/icons/chevron-left";
import { Error as ErrorIcon } from "@material-symbols-svg/react/rounded/icons/error";
import { ProgressActivity } from "@material-symbols-svg/react/rounded/icons/progress-activity";
import { Visibility } from "@material-symbols-svg/react/rounded/icons/visibility";
import { VisibilityOff } from "@material-symbols-svg/react/rounded/icons/visibility-off";
import { type ComponentProps, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { FieldError, FieldLabel } from "@/shared/components/ui/field";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";

// M3 filled text field on the card surface; error state swaps the fill.
// The dark: duplicates beat the Input base's own dark:bg-* in the cascade —
// without them the error fill is invisible in dark mode.
export const AUTH_INPUT_STYLES = cn(
  "h-12 rounded-[16px] border-0 bg-(--schemes-surface-container-high) px-4",
  "dark:bg-(--schemes-surface-container-high)",
  "text-label-medium text-foreground placeholder:text-muted-foreground/70",
  "aria-invalid:bg-(--schemes-error-container) aria-invalid:ring-0",
  "dark:aria-invalid:bg-(--schemes-error-container)",
);

/** Field label in the auth screens' M3 styling, error-aware via the Field group. */
export function AuthFieldLabel({
  className,
  ...props
}: ComponentProps<typeof FieldLabel>) {
  return (
    <FieldLabel
      className={cn(
        "text-label-small-prominent text-muted-foreground group-data-[invalid=true]/field:text-destructive",
        className,
      )}
      {...props}
    />
  );
}

/** Error row per design: filled error icon + message under the input. */
export function AuthFieldError({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  return (
    <FieldError
      id={id}
      className="flex items-center gap-1.5 text-label-small-prominent"
    >
      <ErrorIcon className="size-4 shrink-0" />
      {children}
    </FieldError>
  );
}

/** Password input with the show/hide toggle from the design. */
export function PasswordInput({
  className,
  ...props
}: ComponentProps<typeof Input>) {
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="relative">
      <Input
        {...props}
        type={showPassword ? "text" : "password"}
        className={cn(AUTH_INPUT_STYLES, "pr-12", className)}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={
          showPassword ? t("signIn.hidePassword") : t("signIn.showPassword")
        }
        onClick={() => setShowPassword((current) => !current)}
        // Centered via auto margins, not translate — the Button press effect
        // animates translate-y and would collide with a transform centering.
        className="absolute inset-y-0 right-2 my-auto rounded-full text-muted-foreground"
      >
        {showPassword ? (
          <VisibilityOff className="size-5" />
        ) : (
          <Visibility className="size-5" />
        )}
      </Button>
    </div>
  );
}

type AuthSubmitButtonProps = {
  /** Switches the M3 look from inactive (tonal, dimmed) to primary. */
  active: boolean;
  submitting: boolean;
  children: ReactNode;
};

/** Visually inactive until the form is submittable, but always clickable so
 *  submitting surfaces validation errors (M3 pattern); only a running submit
 *  disables it. */
export function AuthSubmitButton({
  active,
  submitting,
  children,
}: AuthSubmitButtonProps) {
  return (
    <Button
      type="submit"
      disabled={submitting}
      className={cn(
        "h-10 w-full rounded-full text-label-small",
        (!active || submitting) &&
          "bg-(--schemes-surface-container-high) text-muted-foreground opacity-64 disabled:opacity-64",
      )}
    >
      {submitting && (
        <ProgressActivity className="size-4 animate-spin" aria-hidden />
      )}
      {children}
    </Button>
  );
}

/** Title + description block shared by the auth cards. */
export function AuthHeading({
  title,
  description,
}: {
  title: ReactNode;
  description: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-heading-medium tracking-[-0.56px] text-foreground">
        {title}
      </h1>
      <p className="text-label-medium text-muted-foreground">{description}</p>
    </div>
  );
}

/** Tonal back chip returning to the previous auth screen (callback-driven —
 *  no router in the gcip-iap sign-in page). */
export function AuthBackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation("auth");
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className="h-10 self-start rounded-full bg-(--schemes-surface-container-high) pr-4 pl-3 text-label-small text-muted-foreground hover:text-muted-foreground"
    >
      <ChevronLeft className="size-5" />
      {t("back")}
    </Button>
  );
}

/** Elevated provider/option pill (Google, e-mail, …) per the Figma Button. */
export function AuthProviderButton({
  className,
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button
      variant="outline"
      className={cn(
        "state-layer relative isolate h-10 w-full rounded-full border-0 bg-(--schemes-surface-container-low) text-label-small text-muted-foreground shadow-elevation-medium hover:bg-(--schemes-surface-container-low) hover:text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Horizontal rule with the localized "or" label between auth options. */
export function AuthDivider() {
  const { t } = useTranslation("auth");
  return (
    <div className="flex items-center gap-4" role="presentation">
      <div className="h-px flex-1 bg-border" />
      <span className="text-label-small text-muted-foreground">
        {t("divider")}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}
